import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface DataLoadingProps {
  /**
   * When false (default), only render while queries are fetching — for toolbar indicators.
   * When true, always show a spinner — for page/section loading fallbacks that must not go blank.
   */
  always?: boolean;
}

/**
 * Shared loading indicator with two call sites in mind:
 *
 * 1. Toolbar / status chrome (`always` omitted or false)
 *    - Renders only while React Query has in-flight fetches.
 *    - Returns null when idle so the bookings grid toolbar stays clean.
 *    - Hover tooltip lists which query keys are refreshing.
 *
 * 2. Page / section fallback (`always={true}`)
 *    - Always shows a spinner so callers never paint empty content
 *      (e.g. PublicList waiting on the list doc, Roster while the query is empty).
 *    - Still shows the fetching tooltip when something is in flight.
 */
export default function DataLoading({ always = false }: DataLoadingProps) {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryCache();
  const queries = cache.findAll();

  // Query-key labels for the tooltip (first segment of each in-flight queryKey).
  const fetchingList = queries
    .filter((query) => query.state.fetchStatus === "fetching")
    .map((query) => query.queryKey[0]);

  const isFetching = fetchingList.length > 0;

  // Toolbar mode: hide completely when nothing is fetching.
  if (!always && !isFetching) {
    return null;
  }

  const spinner = (
    <Loader2 className="size-6 animate-spin text-primary" aria-label="Loading" />
  );

  return (
    // Extra vertical margin only in `always` mode (page/section placeholders).
    <div
      className={`flex items-center justify-center${always ? " my-4" : ""}`}
    >
      {isFetching ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger render={<span className="inline-flex" />}>
              {spinner}
            </TooltipTrigger>
            <TooltipContent>
              {`Refreshing ${fetchingList.join(", ")}...`}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        // `always` with no active fetches — still show a spinner, just without a tooltip.
        spinner
      )}
    </div>
  );
}
